import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

import '../../stylesheets/components/PostcodeSelect.scss';

/**
 * The address box for any server-side lookup provider — Postio, Loqate.
 *
 * Unlike the Swiftcomplete and Fetchify boxes, no vendor script is loaded and
 * no key reaches the page: this talks to our own /api/v0/addresses, which talks
 * to whichever provider config/initializers/postcode_lookup.rb has live. That
 * is why one component serves them all — the server normalises the answer, so
 * there is nothing provider-shaped left by the time it gets here.
 *
 * Two calls, matching how the services bill. /search runs as you type and is
 * free; /retrieve runs once, on the row you pick, and is the one that costs.
 */

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 250;

export type FormattedAddress = {
  line_1: string;
  line_2: string;
  line_3: string;
  city: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
};

type Suggestion = {
  id: string;
  label: string;
};

type ApiPostcodeSelectProps = {
  onSelect?: (address: FormattedAddress) => void;
};

const apiUrl = (path: string) => {
  const base = ((window as any).API_BASE || '/api/v0/').replace(/\/+$/, '');
  return `${base}${path}`;
};

const ApiPostcodeSelect: React.FC<ApiPostcodeSelectProps> = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isRetrieving, setIsRetrieving] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(false);
  // Only the newest search may write to state — a slow keystroke's reply must
  // not overwrite a faster later one.
  const searchSeqRef = useRef(0);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      searchSeqRef.current += 1;
      setSuggestions([]);
      setIsOpen(false);
      setIsSearching(false);
      return;
    }

    const seq = ++searchSeqRef.current;
    setIsSearching(true);

    const timer = window.setTimeout(() => {
      axios.get(apiUrl('/addresses/search'), { params: { q: trimmed } })
        .then((response) => {
          if (!isMountedRef.current || seq !== searchSeqRef.current) return;

          const results: Suggestion[] = response.data?.result || [];
          setSuggestions(results);
          setHighlighted(-1);
          setIsOpen(true);
          setIsSearching(false);
          setError(null);
        })
        .catch((err) => {
          if (!isMountedRef.current || seq !== searchSeqRef.current) return;

          console.error('Address search failed:', err);
          setSuggestions([]);
          setIsOpen(false);
          setIsSearching(false);
          setError('Address lookup is unavailable. Please enter the address manually.');
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query]);

  // A click anywhere else closes the list. Anything inside is the list's own.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = useCallback((suggestion: Suggestion) => {
    // Stop any in-flight search from reopening the list behind the retrieve.
    searchSeqRef.current += 1;
    setQuery(suggestion.label);
    setIsOpen(false);
    setSuggestions([]);
    setIsRetrieving(true);
    setError(null);

    axios.get(apiUrl('/addresses/retrieve'), { params: { id: suggestion.id } })
      .then((response) => {
        if (!isMountedRef.current) return;

        setIsRetrieving(false);
        const address = response.data?.result;
        if (!address) {
          setError('That address could not be loaded. Please enter it manually.');
          return;
        }

        onSelectRef.current?.({
          line_1: address.line_1 || '',
          line_2: address.line_2 || '',
          line_3: address.line_3 || '',
          city: address.city || '',
          postal_code: address.postal_code || '',
          latitude: typeof address.latitude === 'number' ? address.latitude : null,
          longitude: typeof address.longitude === 'number' ? address.longitude : null,
        });
      })
      .catch((err) => {
        if (!isMountedRef.current) return;

        console.error('Address retrieval failed:', err);
        setIsRetrieving(false);
        setError('That address could not be loaded. Please enter it manually.');
      });
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((i) => (i + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === 'Enter') {
      // Only swallow Enter when it is choosing a row; otherwise let the form
      // submit as it would without this box in the way.
      if (highlighted >= 0) {
        event.preventDefault();
        handleSelect(suggestions[highlighted]);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }, [isOpen, suggestions, highlighted, handleSelect]);

  let className = 'PostcodeSelect';
  if (error) {
    className += ' has-error';
  }

  return (
    <div className={className} ref={containerRef}>
      <div className="PostcodeSelect__field">
        <input
          type="text"
          className="form-control"
          placeholder="Start typing an address or postcode"
          autoComplete="off"
          id="postcode_input"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="postcode_suggestions"
          value={query}
          disabled={isRetrieving}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        />
        {(isSearching || isRetrieving) && (
          <span className="PostcodeSelect__status">
            {isRetrieving ? 'Loading…' : 'Searching…'}
          </span>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="PostcodeSelect__menu" id="postcode_suggestions" role="listbox">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              role="option"
              aria-selected={index === highlighted}
              className={
                'PostcodeSelect__option' + (index === highlighted ? ' is-active' : '')
              }
              // mousedown, not click: the input's blur would close the list
              // before a click ever landed.
              onMouseDown={(e) => { e.preventDefault(); handleSelect(suggestion); }}
              onMouseEnter={() => setHighlighted(index)}
            >
              {suggestion.label}
            </li>
          ))}
        </ul>
      )}

      {isOpen && !isSearching && suggestions.length === 0 && query.trim().length >= MIN_QUERY_LENGTH && (
        <p className="help-block">No addresses found. Try a postcode, or enter the address manually.</p>
      )}

      {error && <p className="help-block">{error}</p>}
    </div>
  );
};

export default ApiPostcodeSelect;
